import { useEffect, useState } from "react";

import classnames from "classnames";
import { noop } from "neetocist";
import { useBreakpoints } from "neetocommons/react-utils";
import { Collapse } from "neetoicons";
import { Button } from "neetoui";
import PropTypes from "prop-types";

import ElementList from "./ElementsList";
import SelectedElementsList from "./SelectedElementsList";

import { PANEL_TYPES } from "../constants";
import "../styles.scss";

const ElementsPanel = ({
  elementSections,
  selectedElements = [],
  handleAddElement,
  isElementVisible,
  fixedFirstElement,
  fixedLastElement,
  anchoredElementIds = [],
  isReorderDisabled = true,
  isDragElementsDisabled = true,
  handleSelectElement,
  selectedElementId,
  selectedElementActions,
  addAllElements = noop,
  isAddingAllElements = false,
  showElementsFillButton = false,
  isAddingNewElement = false,
  addNewElement,
  addNewElementLabel,
  isSelectedElementActionsDisabled = false,
  selectedElementActionsTooltipProps = {},
  renderElementsCount,
}) => {
  const [visiblePanel, setVisiblePanel] = useState(PANEL_TYPES.SUMMARY);
  const { isSize } = useBreakpoints();
  const isLargerScreen = !isSize("mobile");

  const [isElementsPanelCollapsed, setIsElementsPanelCollapsed] =
    useState(!isLargerScreen);

  useEffect(() => {
    setIsElementsPanelCollapsed(!isLargerScreen);
  }, [isLargerScreen]);

  return (
    <>
      {!isElementsPanelCollapsed && (
        <div
          className="neeto-molecules-builder-panel__backdrop"
          onClick={() => {
            setIsElementsPanelCollapsed(!isElementsPanelCollapsed);
          }}
        />
      )}
      <div
        className={classnames(
          "neeto-molecules-builder-elements-list-panel__wrapper",
          {
            "neeto-molecules-builder-elements-list-panel__wrapper--collapsed":
              isElementsPanelCollapsed,
          }
        )}
      >
        <div className="neeto-molecules-builder-elements-list-panel__container">
          <div
            className={classnames(
              "neeto-molecules-builder-elements-list-panel",
              {
                "neeto-ui-bg-white": visiblePanel === PANEL_TYPES.SUMMARY,
                "neeto-ui-bg-gray-50": visiblePanel !== PANEL_TYPES.SUMMARY,
              }
            )}
            onClick={event => event.stopPropagation()}
          >
            {visiblePanel === PANEL_TYPES.SUMMARY ? (
              <SelectedElementsList
                {...{
                  addAllElements,
                  addNewElement,
                  addNewElementLabel,
                  anchoredElementIds,
                  fixedFirstElement,
                  fixedLastElement,
                  handleSelectElement,
                  isAddingAllElements,
                  isAddingNewElement,
                  isElementVisible,
                  isReorderDisabled,
                  isSelectedElementActionsDisabled,
                  renderElementsCount,
                  selectedElementActions,
                  selectedElementActionsTooltipProps,
                  selectedElementId,
                  selectedElements,
                  setVisiblePanel,
                  showElementsFillButton,
                }}
              />
            ) : (
              <ElementList
                {...{
                  elementSections,
                  isAddingNewElement,
                  isDragElementsDisabled,
                  setVisiblePanel,
                }}
                onSelect={handleAddElement}
              />
            )}
          </div>
        </div>
        <Button
          className="neeto-molecules-builder-elements-list-panel__toggler"
          icon={Collapse}
          style="secondary"
          onClick={() => {
            setIsElementsPanelCollapsed(!isElementsPanelCollapsed);
          }}
        />
      </div>
    </>
  );
};

ElementsPanel.propTypes = {
  /**
   * List of supported elements to be rendered in the ElementsList panel.
   */
  elementSections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      elements: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
          icon: PropTypes.elementType,
        })
      ),
      key: PropTypes.string,
      isCreatable: PropTypes.func,
      disabledElementTooltip: PropTypes.func,
    })
  ),
  /**
   * List of selected elements.
   */
  selectedElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      kind: PropTypes.string,
      position: PropTypes.number,
      isPending: PropTypes.bool,
      label: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ),
  /**
   * Callback that will be triggered when an element is clicked from the elements list.
   */
  handleAddElement: PropTypes.func,
  /**
   * Function that will be used to check whether the element is visible.
   * The callback will be triggered with element info at the time of render.
   */
  isElementVisible: PropTypes.func,
  /**
   * The record that's to be rendered as first record.
   * This won't be draggable.
   */
  fixedFirstElement: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        kind: PropTypes.string,
        position: PropTypes.number,
        label: PropTypes.string,
        icon: PropTypes.node,
        actions: PropTypes.oneOfType([
          PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              key: PropTypes.string,
              onClick: PropTypes.func,
              isVisible: PropTypes.bool,
            })
          ),
          PropTypes.func,
        ]),
      })
    ),
    PropTypes.shape({
      id: PropTypes.string,
      kind: PropTypes.string,
      position: PropTypes.number,
      label: PropTypes.string,
      icon: PropTypes.node,
      actions: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            key: PropTypes.string,
            onClick: PropTypes.func,
            isVisible: PropTypes.bool,
          })
        ),
        PropTypes.func,
      ]),
    }),
  ]),
  /**
   * The record that's to be rendered as the last record.
   * This won't be draggable.
   */
  fixedLastElement: PropTypes.shape({
    id: PropTypes.string,
    kind: PropTypes.string,
    position: PropTypes.number,
    label: PropTypes.string,
    icon: PropTypes.node,
  }),
  /**
   * List of ids of elements that cannot be dragged.
   * Some specific elements can be dragged and dropped over them.
   */
  anchoredElementIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Boolean to mention that the drag and order selected elements feature should be disabled.
   */
  isReorderDisabled: PropTypes.bool,
  /**
   * Boolean to mention that the drag and drop elements to the preview container is enabled.
   */
  isDragElementsDisabled: PropTypes.bool,
  /**
   * The callback that will be triggered when an element is clicked from the elements list.
   */
  handleSelectElement: PropTypes.func,
  /**
   * Id of the current selected element.
   */
  selectedElementId: PropTypes.string,
  /**
   * Actions to be rendered on the selected elements.
   */
  selectedElementActions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        key: PropTypes.string,
        onClick: PropTypes.func,
        isVisible: PropTypes.bool,
      })
    ),
    PropTypes.func,
  ]),

  /**
   * Function that will be called when clicked on the add all elements button.
   */
  addAllElements: PropTypes.func,
  /**
   * Boolean to notate whether all elements are being added.
   */
  isAddingAllElements: PropTypes.bool,
  /**
   * Boolean to control the visibility of add all elements button.
   */
  showElementsFillButton: PropTypes.bool,
  /**
   * Boolean to notate whether an element is being added.
   */
  isAddingNewElement: PropTypes.bool,
  /**
   * Prop to customize the label of the "Add new element" button.
   */
  addNewElementLabel: PropTypes.string,
  /**
   * Function that will be called when clicking on add new element button. If not provided, will default to showing the elements panel.
   */
  addNewElement: PropTypes.func,
  /**
   * Boolean to disable the selected element actions.
   */
  isSelectedElementActionsDisabled: PropTypes.bool,
  /**
   * Props to be passed to the tooltip component for selected element actions.
   */
  selectedElementActionsTooltipProps: PropTypes.object,
  /**
   * Render prop called with the visible elements count. When provided, its return value is rendered
   * below the reorder description (e.g. (count) => <Typography>{t('key', { count })}</Typography>).
   */
  renderElementsCount: PropTypes.func,
};

export default ElementsPanel;
